import { getQuickFilterParamsForField } from '@modules/queryBuilder/helpers';
import { useAddQuickFilter } from '@modules/queryBuilder/hooks';
import { FilterListRounded } from '@mui/icons-material';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';
import { trackQuickFiltersEvents } from '../helpers/trackQuickFiltersEvents';
import { useChartTooltipStore } from '../hooks';

interface AddToFilterProps {
  isMenuItem?: boolean;
}

export function AddToFilter({ isMenuItem = false }: AddToFilterProps) {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const addQuickFilterV2 = useAddQuickFilter();

  const {
    fieldName,
    qbField,
    payload,
    shouldShowAddToFilter,
    baseWhereClause,
    dataPointWhereClause,
    dateRange,
  } = useChartTooltipStore(
    ({
      fieldName,
      payload,
      shouldShowAddToFilter,
      qbField,
      baseWhereClause,
      dataPointWhereClause,
      dateRange,
    }) => ({
      fieldName,
      payload,
      shouldShowAddToFilter,
      qbField,
      baseWhereClause,
      dataPointWhereClause,
      dateRange,
    }),
    shallow,
  );

  const eqlExpression = useGetEQLExpression({
    tenantID,
    eqlProps: {
      expressionType: ExpressionType.Aggregates,
      dateRange,
      whereClause: combineWhereClauses(
        baseWhereClause,
        dataPointWhereClause,
        OperationType.AND,
      ),
    },
  });

  const field = qbField || fieldName;

  if (!field || !payload?.id) {
    return null;
  }

  const appendFilter = () => {
    if (fieldName && payload?.id) {
      const quickFilterParams = getQuickFilterParamsForField({
        fieldName,
        metadataQbField: qbField || undefined,
        value: payload.id,
        category: payload.category,
      });
      addQuickFilterV2(quickFilterParams);
      trackQuickFiltersEvents({
        eventName: 'Quick Filter Added',
        eqlExpression: eqlExpression.eql,
        triggerType: 'Chart',
      });
    }
  };

  if (!shouldShowAddToFilter) {
    return null;
  }

  if (isMenuItem) {
    return (
      <MenuItem onClick={appendFilter}>
        <ListItemIcon>
          <FilterListRounded />
        </ListItemIcon>
        <ListItemText primary="Add to Filter" />
      </MenuItem>
    );
  }

  return (
    <ListItem divider disablePadding onClick={appendFilter}>
      <ListItemButton>
        <ListItemIcon>
          <FilterListRounded />
        </ListItemIcon>
        <ListItemText primary="Add to Filter" />
      </ListItemButton>
    </ListItem>
  );
}
